import { ballIsVisible } from './data';
import { ContextCloseBar, contextPanelPosition } from './ContextPanel';

/**
 * BallPanel — a ball's own properties, shown while that ball is selected.
 *
 * Controls:
 *   • Colour  — the ball's look on the pitch (hex).
 *   • Visible — hides the ball completely. A hidden ball is not drawn AND not
 *     clickable in the viewport (Viewport3D's picking ignores hidden objects),
 *     so it can only be brought back with this checkbox (or by loading the
 *     scenario again).
 *
 * `visible` is optional in stored scenarios: scenarios saved before the flag
 * existed have no value, and a missing value means visible — see
 * `ballIsVisible()`.
 *
 * @param {object}   props.ball     - ball state ({ id, color, position, visible })
 * @param {function} props.onChange - (patch) => void, e.g. { visible: false }
 * @param {function} [props.onClose]- closes the panel
 * @param {function} [props.t]      - i18n lookup
 * @param {number}   [props.bottom] - distance from the bottom of the viewport
 */
export default function BallPanel({ ball, onChange, onClose, t, bottom = 66 }) {
    if (!ball) return null;
    const tr = t || ((k) => k);
    const visible = ballIsVisible(ball);
    const lbl = { color: '#94a3b8', fontSize: 11, fontWeight: 700 };

    return (
        <div className="kv-ball-panel" style={{
            ...contextPanelPosition(bottom),
            width: 'max-content', maxWidth: 'min(200px, calc(100vw - 16px))', boxSizing: 'border-box',
            background: '#0f172a', border: '1px solid #334155', borderRadius: 12,
            padding: 0, zIndex: 250, boxShadow: '0 8px 30px rgba(0,0,0,0.5)', color: '#e2e8f0',
        }}>
            <ContextCloseBar label={tr('ball.close')} onClick={onClose} />
            <div style={{ padding: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 800, color: '#e2e8f0' }}>{tr('ball.title')}</span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 4 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, minHeight: 32 }}>
                <span style={lbl}>{tr('ball.colour')}</span>
                <input type="color" aria-label={tr('ball.colour')} value={ball.color || '#ffffff'}
                    onChange={(e) => onChange && onChange({ color: e.target.value })}
                    style={{ width: 32, height: 32, border: 'none', borderRadius: 6, background: 'transparent', cursor: 'pointer' }} />
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', minHeight: 32 }}>
                <input type="checkbox" checked={visible}
                    onChange={(e) => onChange && onChange({ visible: e.target.checked })}
                    style={{ width: 16, height: 16, accentColor: '#3b82f6', cursor: 'pointer', flexShrink: 0 }} />
                <span style={{ fontSize: 12, fontWeight: 700 }}>{tr('ball.visible')}</span>
            </label>
            </div>
            {/* The ball's two richest verbs happen ON the pitch, not in this
                panel — teach them where the user is already looking. */}
            <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.45, marginTop: 6, maxWidth: 190 }}>
                {tr('ball.attachHint')}
            </div>
            {!visible && (
                <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.45, marginTop: 6, maxWidth: 190 }}>
                    {tr('ball.hiddenHint')}
                </div>
            )}
            </div>
        </div>
    );
}
